import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import { sortByName } from './scheduler-ui.js';

export interface DriverAssignment {
  terminal_id?: string | number;
  terminal_name?: string;
  terminal_number?: string | null;
  assignment_type?: string;
  started_at?: string;
  notes?: string;
}

export interface DriverConstraint {
  id: string | number;
  constraint_type: string;
  day_of_week?: number;
  description?: string;
}

export interface TimeOffRequest {
  id: string | number;
  status?: string;
  employee_name?: string;
  request_type?: string;
  start_date?: string;
  end_date?: string;
  reason?: string;
  decision_notes?: string;
  created_at?: string;
}

export interface SchedulerDriver {
  id: string | number;
  first_name?: string;
  last_name?: string;
  driver_id?: string;
  staffing_type?: string;
  role?: string;
  current_assignment?: DriverAssignment | null;
  assigned_terminal_id?: string | number;
  assigned_terminal_name?: string;
  assigned_terminal_number?: string;
  constraints?: DriverConstraint[];
  time_off_requests?: TimeOffRequest[];
  pto_balance?: { annual_days: number; used_days: number };
  pto_eligible?: boolean;
}

export interface TerminalOption {
  id: string;
  name: string;
  number?: string | null;
  isActive?: boolean;
}

interface TerminalGroup {
  terminal: TerminalOption;
  drivers: SchedulerDriver[];
}

export interface AssignmentGroups {
  home: SchedulerDriver[];
  offRotation: SchedulerDriver[];
  unassigned: SchedulerDriver[];
  byTerminal: Record<string, TerminalGroup>;
}

export interface GridDay {
  date?: string;
  status?: string;
  terminal?: { name?: string; number?: string; code?: string };
  type?: string;
  override_id?: string | number;
  note?: string | null;
  is_day_override?: boolean;
}

export interface GridRow {
  employee: SchedulerDriver;
  days: GridDay[];
}

export interface GridData {
  days?: string[];
  grid?: GridRow[];
}

interface RosterResponse { roster?: SchedulerDriver[]; error?: string }
interface TimeOffResponse { requests?: TimeOffRequest[]; error?: string }
interface TerminalListResponse {
  id: string | number;
  name?: string;
  code?: string;
  fedex_code?: string;
  fedex_number?: string | null;
  is_active?: boolean;
}

interface ResolvedSchedulerTerminal {
  id: string;
  name: string;
  number?: string | null;
  source: 'assignment' | 'mirror';
}

export function resolveSchedulerTerminal(driver: SchedulerDriver): ResolvedSchedulerTerminal | null {
  const currentAssignment = driver.current_assignment || null;
  if (currentAssignment?.assignment_type === 'terminal' && currentAssignment.terminal_id) {
    return {
      id: String(currentAssignment.terminal_id),
      name: currentAssignment.terminal_name || driver.assigned_terminal_name || `Terminal ${currentAssignment.terminal_id}`,
      number: currentAssignment.terminal_number || driver.assigned_terminal_number || null,
      source: 'assignment',
    };
  }

  if (driver.assigned_terminal_id) {
    return {
      id: String(driver.assigned_terminal_id),
      name: driver.assigned_terminal_name || `Terminal ${driver.assigned_terminal_id}`,
      number: driver.assigned_terminal_number || null,
      source: 'mirror',
    };
  }

  return null;
}

function buildAssignmentGroups(roster: SchedulerDriver[], selectedTerminalId: string, terminals: TerminalOption[]): AssignmentGroups {
  const groups: AssignmentGroups = {
    home: [],
    offRotation: [],
    unassigned: [],
    byTerminal: {},
  };

  terminals
    .filter((terminal) => terminal.id !== 'all' && terminal.isActive !== false)
    .forEach((terminal) => {
      groups.byTerminal[terminal.id] = { terminal, drivers: [] };
    });

  roster.forEach((driver) => {
    const assignment = driver.current_assignment || null;
    const resolvedTerminal = resolveSchedulerTerminal(driver);

    if (selectedTerminalId && selectedTerminalId !== 'all') {
      if (String(resolvedTerminal?.id || '') !== String(selectedTerminalId)) return;
    }

    if (assignment?.assignment_type === 'home') {
      groups.home.push(driver);
      return;
    }

    if (assignment?.assignment_type === 'off_rotation') {
      groups.offRotation.push(driver);
      return;
    }

    if (!resolvedTerminal) {
      groups.unassigned.push(driver);
      return;
    }

    if (!groups.byTerminal[resolvedTerminal.id]) {
      groups.byTerminal[resolvedTerminal.id] = {
        terminal: {
          id: resolvedTerminal.id,
          name: resolvedTerminal.name,
          number: resolvedTerminal.number || null,
          isActive: true,
        },
        drivers: [],
      };
    }

    groups.byTerminal[resolvedTerminal.id].drivers.push(driver);
  });

  groups.home.sort(sortByName);
  groups.offRotation.sort(sortByName);
  groups.unassigned.sort(sortByName);
  Object.values(groups.byTerminal).forEach((group) => group.drivers.sort(sortByName));

  return groups;
}

export function useSchedulerReadModel(weekStart: string, selectedTerminalId: string) {
  const [roster, setRoster] = useState<SchedulerDriver[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [gridData, setGridData] = useState<GridData | null>(null);
  const [terminals, setTerminals] = useState<TerminalOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [rosterData, timeOffData, gridResponse, terminalData] = await Promise.all([
        apiFetch<RosterResponse>('/api/scheduler/roster'),
        apiFetch<TimeOffResponse>('/api/scheduler/time-off'),
        apiFetch<GridData & { error?: string }>(`/api/scheduler/grid?week_start=${weekStart}`),
        apiFetch<(TerminalListResponse & { error?: string })[]>('/api/terminals'),
      ]);

      if (rosterData?.error) throw new Error(rosterData.error);
      if (timeOffData?.error) throw new Error(timeOffData.error);
      if (gridResponse?.error) throw new Error(gridResponse.error);
      if (!Array.isArray(terminalData)) throw new Error('Terminal list load failed');

      setRoster((rosterData?.roster || []).slice().sort(sortByName));
      setTimeOffRequests((timeOffData?.requests || []).slice().sort((a, b) => {
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      }));
      setGridData(gridResponse || null);
      setTerminals(
        terminalData
          .map((terminal) => ({
            id: String(terminal.id),
            name: terminal.name || terminal.code || terminal.fedex_code || `Terminal ${terminal.id}`,
            number: terminal.fedex_number || null,
            isActive: terminal.is_active !== false,
          }))
          .filter((terminal) => terminal.isActive !== false)
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (err) {
      setRoster([]);
      setTimeOffRequests([]);
      setGridData(null);
      setTerminals([]);
      setError(err instanceof Error ? err.message : 'Scheduler read load failed');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    load();
  }, [load, weekStart]);

  const terminalOptions = useMemo<TerminalOption[]>(() => {
    return [{ id: 'all', name: 'All terminals' }, ...terminals];
  }, [terminals]);

  const assignmentGroups = useMemo(() => buildAssignmentGroups(roster, selectedTerminalId, terminalOptions), [roster, selectedTerminalId, terminalOptions]);

  const pendingRequests = useMemo(() => timeOffRequests.filter((request) => request.status === 'pending'), [timeOffRequests]);
  const decidedRequests = useMemo(() => timeOffRequests.filter((request) => request.status && request.status !== 'pending'), [timeOffRequests]);
  const ptoEligible = useMemo(() => roster.filter((driver) => driver.pto_eligible), [roster]);

  return {
    roster,
    timeOffRequests,
    assignmentGroups,
    terminalOptions,
    pendingRequests,
    decidedRequests,
    ptoEligible,
    gridData,
    loading,
    error,
    refreshScheduler: load,
  };
}
